import { BaseElement } from '../../base-element';
import { Signal } from 'signal-polyfill';
import { adminApi } from '../../../api/admin.api';
import type {
  CreateTrialEventNoteRequestDto,
  TrialEventAdminDetailDto,
  TrialEventNoteDto,
  TrialEventUpsertRequestDto,
} from '../../../api/types';
import { hasActualTrialConfiguration } from '../../../api/actual-trials';
import {
  configurationErrors, divisions, newEventDraft, permittedStocks, stockNames, syncOfferSummary,
  type EventDraft, type TrialDraft,
} from './trial-event-configuration';
import { authState, canManageTrialEvents } from '../../../state/auth.state';
import { navigate } from '../../../router';
import '../../ui/dt-badge';
import '../../ui/dt-button';
import '../../ui/dt-card';
import '../../ui/dt-input';
import '../../ui/dt-spinner';

type PageState = 'loading' | 'loaded' | 'error' | 'forbidden';

type InputElement = HTMLElement & {
  value: string;
  error?: string | null;
};

type DtButtonElement = HTMLElement & {
  disabled?: boolean;
  loading?: boolean;
};

interface TextFieldConfig {
  field: keyof TrialEventUpsertRequestDto;
  label: string;
  type?: 'text' | 'email' | 'date';
  nullable?: boolean;
}

interface NumberFieldConfig {
  field: keyof TrialEventUpsertRequestDto;
  label: string;
  step?: string;
  nullable?: boolean;
}

interface CheckboxFieldConfig {
  field: keyof TrialEventUpsertRequestDto;
  label: string;
}

const overviewFields: TextFieldConfig[] = [
  { field: 'name', label: 'Trial Event Name' },
  { field: 'hostClubName', label: 'Host Club Name' },
  { field: 'location', label: 'Legacy Location', nullable: true },
  { field: 'dogBreeds', label: 'Eligible Dog Breeds' },
];

const dateFields: TextFieldConfig[] = [
  { field: 'startDate', label: 'Start Date', type: 'date' },
  { field: 'endDate', label: 'End Date', type: 'date' },
  {
    field: 'preEntriesOpenDateForAustralianShepherds',
    label: 'Australian Shepherd Pre-Entries Open',
    type: 'date',
  },
  {
    field: 'preEntriesOpenDateForOtherBreeds',
    label: 'Other Breeds Pre-Entries Open',
    type: 'date',
  },
  { field: 'entriesCloseDate', label: 'Entries Close Date', type: 'date' },
  { field: 'drawForRunOrder', label: 'Draw For Run Order' },
];

const venueFields: TextFieldConfig[] = [
  { field: 'addressTitle', label: 'Venue Title' },
  { field: 'addressLine1', label: 'Address Line 1' },
  { field: 'addressLine2', label: 'Address Line 2', nullable: true },
  { field: 'addressCity', label: 'City' },
  { field: 'addressState', label: 'State' },
  { field: 'addressPostalCode', label: 'Postal Code' },
  { field: 'secretaryName', label: 'Secretary Name' },
  { field: 'secretaryEmail', label: 'Secretary Email', type: 'email' },
];

const numberFields: NumberFieldConfig[] = [
  { field: 'weekendDiscountPercent', label: 'Weekend Discount Percent', step: '0.01', nullable: true },
  { field: 'weekendDiscountAmount', label: 'Weekend Discount Amount', step: '0.01', nullable: true },
  {
    field: 'juniorHandlersDiscountPercent',
    label: 'Junior Handler Discount Percent',
    step: '0.01',
    nullable: true,
  },
  {
    field: 'juniorHandlersDiscountAmount',
    label: 'Junior Handler Discount Amount',
    step: '0.01',
    nullable: true,
  },
];

const checkboxFields: CheckboxFieldConfig[] = [
  { field: 'preEntriesOnly', label: 'Pre-Entries Only' },
  { field: 'openToAllBreedsOnSameDate', label: 'Open To All Breeds On Same Date' },
];

const identityFields: TextFieldConfig[] = [
  { field: 'organizationCode', label: 'Organization Code' },
  { field: 'sportCode', label: 'Sport Code' },
  { field: 'formCode', label: 'Form Code' },
  { field: 'formVersion', label: 'Form Version' },
  { field: 'clubSlug', label: 'Club Slug' },
  { field: 'eventSlug', label: 'Event Slug' },
];
const mailFields: TextFieldConfig[] = [
  { field: 'entryMailAddressLine1', label: 'Entry Mail Address Line 1' },
  { field: 'entryMailAddressLine2', label: 'Entry Mail Address Line 2', nullable: true },
  { field: 'entryMailAddressCity', label: 'Entry Mail City' },
  { field: 'entryMailAddressState', label: 'Entry Mail State' },
  { field: 'entryMailAddressPostalCode', label: 'Entry Mail Postal Code' },
];
const requiredFields = [...identityFields, ...overviewFields, ...dateFields, ...venueFields, ...mailFields]
  .filter(config => !config.nullable).map(config => config.field);

const nullableStringFields = new Set<keyof TrialEventUpsertRequestDto>([
  'location',
  'addressLine2',
  'addressDirections',
  'dayOfEntriesStartTime',
  'dayOfEntriesEndTime',
]);

function toNumber(value: number | string | null | undefined): number {
  if (typeof value === 'number') {
    return value;
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  return 0;
}

function toNullableNumber(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  return toNumber(value);
}

function normalizeTime(value: string | null | undefined): string | null {
  if (!value) {
    return null;
  }

  return value;
}

function createDraft(detail: TrialEventAdminDetailDto): TrialEventUpsertRequestDto {
  return {
    trialType: detail.trialType,
    offersStockSheep: detail.offersStockSheep,
    offersStockCattle: detail.offersStockCattle,
    offersStockDucks: detail.offersStockDucks,
    offersStockMixed: detail.offersStockMixed,
    allowsFeo: detail.allowsFeo,
    actualTrials: structuredClone(detail.actualTrials),
    name: detail.name ?? '',
    organizationCode: detail.organizationCode ?? '',
    sportCode: detail.sportCode ?? '',
    formCode: detail.formCode ?? '',
    formVersion: detail.formVersion ?? '',
    clubSlug: detail.clubSlug ?? '',
    eventSlug: detail.eventSlug ?? '',
    hostClubName: detail.hostClubName ?? '',
    startDate: detail.startDate ?? '',
    endDate: detail.endDate ?? '',
    totalTrialsAtEvent: toNumber(detail.totalTrialsAtEvent),
    addressTitle: detail.addressTitle ?? '',
    addressDirections: detail.addressDirections ?? null,
    addressLine1: detail.addressLine1 ?? '',
    addressLine2: detail.addressLine2 ?? null,
    addressCity: detail.addressCity ?? '',
    addressState: detail.addressState ?? '',
    addressPostalCode: detail.addressPostalCode ?? '',
    dogBreeds: detail.dogBreeds ?? '',
    openToAllBreedsOnSameDate: detail.openToAllBreedsOnSameDate ?? false,
    preEntriesOpenDateForAustralianShepherds:
      detail.preEntriesOpenDateForAustralianShepherds ?? '',
    preEntriesOpenDateForOtherBreeds: detail.preEntriesOpenDateForOtherBreeds ?? '',
    entriesCloseDate: detail.entriesCloseDate ?? '',
    preEntriesOnly: detail.preEntriesOnly ?? false,
    dayOfEntriesStartTime: normalizeTime(detail.dayOfEntriesStartTime),
    dayOfEntriesEndTime: normalizeTime(detail.dayOfEntriesEndTime),
    drawForRunOrder: detail.drawForRunOrder ?? '',
    entryMailAddressLine1: detail.entryMailAddressLine1 ?? '',
    entryMailAddressLine2: detail.entryMailAddressLine2 ?? null,
    entryMailAddressCity: detail.entryMailAddressCity ?? '',
    entryMailAddressState: detail.entryMailAddressState ?? '',
    entryMailAddressPostalCode: detail.entryMailAddressPostalCode ?? '',
    secretaryName: detail.secretaryName ?? '',
    secretaryEmail: detail.secretaryEmail ?? '',
    weekendDiscountPercent: toNullableNumber(detail.weekendDiscountPercent),
    weekendDiscountAmount: toNullableNumber(detail.weekendDiscountAmount),
    juniorHandlersDiscountPercent: toNullableNumber(detail.juniorHandlersDiscountPercent),
    juniorHandlersDiscountAmount: toNullableNumber(detail.juniorHandlersDiscountAmount),
    classesIncludeStarted: detail.classesIncludeStarted ?? false,
    classesIncludeOpen: detail.classesIncludeOpen ?? false,
    classesIncludeAdvanced: detail.classesIncludeAdvanced ?? false,
    classesIncludeFtdOpen: detail.classesIncludeFtdOpen ?? false,
    classesIncludeFtdAdv: detail.classesIncludeFtdAdv ?? false,
    classesIncludeNovice: detail.classesIncludeNovice ?? false,
    classesIncludeJunior: detail.classesIncludeJunior ?? false,
    classesIncludePostAdvanced: detail.classesIncludePostAdvanced ?? false,
    classesIncludeRtd: detail.classesIncludeRtd ?? false,
    location: detail.location ?? null,
  };
}

export class TrialEventDetailPage extends BaseElement {
  private trialEventId = '';
  private draft: EventDraft | null = null;
  private configurationNotice = '';
  private creating = false;
  private detail = new Signal.State<TrialEventAdminDetailDto | null>(null);
  private notes = new Signal.State<TrialEventNoteDto[]>([]);
  private notesLoading = new Signal.State(false);
  private notesError = new Signal.State<string | null>(null);
  private noteDraft = new Signal.State('');
  private noteSubmitting = new Signal.State(false);
  private noteSubmitError = new Signal.State<string | null>(null);
  private courseDirectorEmailDraft = new Signal.State('');
  private courseDirectorStatus = new Signal.State<{ tone: 'success' | 'error'; message: string } | null>(null);
  private courseDirectorSubmitting = new Signal.State(false);
  private courseDirectorRemovingEmail = new Signal.State<string | null>(null);
  private loading = new Signal.State(false);
  private forbidden = new Signal.State(false);
  private loadError = new Signal.State<string | null>(null);
  private actionError = new Signal.State<string | null>(null);
  private validationErrors = new Signal.State<Record<string, string[]>>({});
  private saving = new Signal.State(false);
  private lifecycleAction = new Signal.State<'deactivate' | 'reactivate' | null>(null);
  private selectedPremiumPdf = new Signal.State<File | null>(null);

  private pageState = new Signal.Computed<PageState>(() => {
    const auth = authState.get();
    if (!auth.isAuthenticated || !canManageTrialEvents.get() || this.forbidden.get()) {
      return 'forbidden';
    }

    if (this.loading.get()) {
      return 'loading';
    }

    if (this.loadError.get()) {
      return 'error';
    }

    return 'loaded';
  });

  static get observedAttributes(): string[] {
    return ['trial-event-id', 'create'];
  }

  attributeChangedCallback(name: string, _oldValue: string, newValue: string): void {
    if (name === 'create') {
      this.creating = newValue !== null;
      if (this.creating) this.draft = newEventDraft();
    }
    if (name === 'trial-event-id') {
      this.trialEventId = newValue || '';
      if (this.isConnected && this.trialEventId) {
        void this.loadDetail();
      }
    }
  }

  connectedCallback(): void {
    // watch invokes its callback immediately; render once after all subscriptions exist.
    let initialized = false;
    const updateRender = () => {
      if (initialized) this.updateRender();
    };
    this.watch(this.detail, updateRender);
    this.watch(this.notes, updateRender);
    this.watch(this.notesLoading, updateRender);
    this.watch(this.notesError, updateRender);
    this.watch(this.noteDraft, updateRender);
    this.watch(this.noteSubmitting, updateRender);
    this.watch(this.noteSubmitError, updateRender);
    this.watch(this.courseDirectorEmailDraft, updateRender);
    this.watch(this.courseDirectorStatus, updateRender);
    this.watch(this.courseDirectorSubmitting, updateRender);
    this.watch(this.courseDirectorRemovingEmail, updateRender);
    this.watch(this.loading, updateRender);
    this.watch(this.forbidden, updateRender);
    this.watch(this.loadError, updateRender);
    this.watch(this.actionError, updateRender);
    this.watch(this.validationErrors, updateRender);
    this.watch(this.saving, updateRender);
    this.watch(this.lifecycleAction, updateRender);
    this.watch(this.selectedPremiumPdf, updateRender);
    this.watch(authState, updateRender);
    this.watch(canManageTrialEvents, updateRender);
    initialized = true;
    this.updateRender();

    if (this.trialEventId) {
      void this.loadDetail();
    }
  }

  private getEditableFields(): Record<string, boolean> {
    return this.detail.get()?.editableFields ?? {};
  }

  private isEditable(fieldName: string): boolean {
    return this.creating || (this.getEditableFields()[fieldName] ?? false);
  }

  private getFieldError(fieldName: string): string | null {
    const errors = this.validationErrors.get();
    return (errors[fieldName] ?? errors[fieldName.charAt(0).toLowerCase() + fieldName.slice(1)])?.[0] ?? null;
  }

  private lockReason(field: string): string {
    return this.detail.get()?.fieldLockReasons[field] ?? 'This field is not editable in the current server state.';
  }

  private canSave(): boolean {
    if (!this.draft || this.saving.get() || this.lifecycleAction.get()) return false;
    if (Object.keys(configurationErrors(this.draft)).length) return false;
    if (requiredFields.some(field => !String(this.draft?.[field] ?? '').trim())) return false;
    if (this.creating && !this.selectedPremiumPdf.get()) return false;
    return true;
  }

  private refreshSaveAvailability(): void {
    const save = this.$('[data-testid="save-btn"]') as DtButtonElement | null;
    if (save) save.disabled = !this.canSave();
    const completeness = this.$('[data-completeness]');
    if (completeness && this.draft) {
      const missing = requiredFields.filter(field => !String(this.draft?.[field] ?? '').trim());
      const invalid = Object.entries(configurationErrors(this.draft)).map(([field, errors]) =>
        `${field}: ${errors[0]}`);
      completeness.textContent = [
        missing.length ? `Complete required fields: ${missing.join(', ')}.` : '',
        ...invalid,
        this.creating && !this.selectedPremiumPdf.get() ? 'A premium PDF is required.' : '',
      ].filter(Boolean).join(' ') || 'Required configuration is complete. The server validates all changes on save.';
    }
  }

  private async loadDetail(): Promise<void> {
    if (!this.trialEventId) {
      this.loadError.set('Missing trial event identifier.');
      return;
    }

    this.notes.set([]);
    this.notesLoading.set(false);
    this.notesError.set(null);
    this.noteDraft.set('');
    this.noteSubmitting.set(false);
    this.noteSubmitError.set(null);
    this.courseDirectorEmailDraft.set('');
    this.courseDirectorStatus.set(null);
    this.courseDirectorSubmitting.set(false);
    this.courseDirectorRemovingEmail.set(null);
    this.loading.set(true);
    this.forbidden.set(false);
    this.loadError.set(null);
    this.actionError.set(null);
    this.validationErrors.set({});

    const response = await adminApi.trialEvents.getById(this.trialEventId);
    if (response.error) {
      this.detail.set(null);
      this.loading.set(false);

      if (response.error.status === 403 || response.error.status === 401) {
        this.forbidden.set(true);
        return;
      }

      this.loadError.set(response.error.title || 'Unable to load trial event details.');
      return;
    }

    if (!response.data || !hasActualTrialConfiguration(response.data) ||
        !divisions.every(division => typeof response.data![division.field] === 'boolean') ||
        !response.data.editableFields || !response.data.fieldLockReasons) {
      this.detail.set(null);
      this.loading.set(false);
      this.loadError.set('Unable to load actual-trial configuration. Please reload the application.');
      return;
    }

    this.detail.set(response.data);
    this.draft = createDraft(response.data);
    this.selectedPremiumPdf.set(null);
    this.loading.set(false);
    void this.loadNotes();
  }

  private async loadNotes(): Promise<void> {
    if (!this.trialEventId) {
      return;
    }

    this.notesLoading.set(true);
    this.notesError.set(null);

    const response = await adminApi.trialEvents.listNotes(this.trialEventId);
    this.notesLoading.set(false);

    if (response.error) {
      if (response.error.status === 403 || response.error.status === 401) {
        this.forbidden.set(true);
        return;
      }

      this.notes.set([]);
      this.notesError.set(response.error.title || 'Unable to load trial event notes.');
      return;
    }

    this.notes.set(response.data ?? []);
  }

  private updateDraftField(field: keyof TrialEventUpsertRequestDto, value: unknown): void {
    if (!this.draft) {
      return;
    }

    this.draft = {
      ...this.draft,
      [field]: value,
    };
    this.refreshSaveAvailability();
  }

  private handleTextInput = (event: Event): void => {
    const target = event.currentTarget as HTMLElement;
    const field = target.getAttribute('data-field') as keyof TrialEventUpsertRequestDto | null;
    const detail = (event as CustomEvent<{ value: string }>).detail;
    if (!field) {
      return;
    }

    const rawValue = detail?.value ?? '';
    const value = nullableStringFields.has(field) && rawValue.trim().length === 0 ? null : rawValue;
    this.updateDraftField(field, value);
  };

  private handleNumberInput = (event: Event): void => {
    const target = event.currentTarget as HTMLInputElement;
    const field = target.getAttribute('data-field') as keyof TrialEventUpsertRequestDto | null;
    if (!field) {
      return;
    }

    const isNullable = target.hasAttribute('data-nullable');
    if (isNullable && target.value.trim().length === 0) {
      this.updateDraftField(field, null);
      return;
    }

    const parsed = Number(target.value);
    this.updateDraftField(field, parsed);
  };

  private handleTimeInput = (event: Event): void => {
    const target = event.currentTarget as HTMLInputElement;
    const field = target.getAttribute('data-field') as keyof TrialEventUpsertRequestDto | null;
    if (!field) {
      return;
    }

    this.updateDraftField(field, target.value.trim().length === 0 ? null : target.value);
  };

  private handleCheckboxInput = (event: Event): void => {
    const target = event.currentTarget as HTMLInputElement;
    const field = target.getAttribute('data-field') as keyof TrialEventUpsertRequestDto | null;
    if (!field) {
      return;
    }

    this.updateDraftField(field, target.checked);
  };

  private handleTypeChange = (event: Event): void => {
    const select = event.currentTarget as HTMLSelectElement;
    const type = select.value;
    if (!this.draft || !['Arena', 'Farm', 'Ranch', 'PostAdvanced'].includes(type) ||
        !this.isEditable('TrialType')) return;
    const next = type as TrialEventUpsertRequestDto['trialType'];
    const removed = [
      ...divisions.filter(division => this.draft![division.field] &&
        !(division.types as readonly string[]).includes(next)).map(division => division.label),
      ...stockNames.filter(stock => this.draft![`offersStock${stock}`] &&
        !permittedStocks(next).includes(stock)).map(stock => `${stock} offers, counts and rates`),
    ];
    if (removed.length && !window.confirm(`Change trial type to ${next}? This clears: ${removed.join(', ')}. Cancel keeps all current values.`)) {
      select.value = this.draft.trialType ?? '';
      return;
    }
    this.draft.trialType = next;
    for (const division of divisions) {
      const permitted = (division.types as readonly string[]).includes(next);
      this.draft[division.field] = permitted && (division.required || this.draft[division.field]);
    }
    for (const trial of this.draft.actualTrials) {
      trial.stocks = trial.stocks!.filter(offer => permittedStocks(next).includes(offer.stock ?? ''));
    }
    syncOfferSummary(this.draft);
    this.configurationNotice = removed.length ? `Cleared incompatible values: ${removed.join(', ')}.` :
      `Selected ${next}. Required competitive divisions are enabled.`;
    this.updateRender();
    this.querySelector<HTMLSelectElement>('[data-testid="trial-type"]')?.focus();
  };

  private trialPath(trial: TrialDraft, index: number): string {
    return `actualTrials[${trial.actualTrialId ?? index}]`;
  }

  private handleTrialInput = (event: Event): void => {
    const input = event.currentTarget as HTMLInputElement | HTMLSelectElement;
    const index = Number(input.dataset.trial);
    const trial = this.draft?.actualTrials[index];
    if (!trial || !this.draft) return;
    const field = input.dataset.trialField;
    if (field === 'date') trial.date = input.value;
    if (field === 'trialNumberOnDate') trial.trialNumberOnDate = input.value ? Number(input.value) : null;
    const stock = trial.stocks?.[Number(input.dataset.stock)];
    if (stock && (field === 'headCount' || field === 'preEntryFee' || field === 'dayOfFee')) {
      stock[field] = input.value === '' ? null : Number(input.value);
      const hint = input.parentElement?.querySelector('[data-presence]');
      if (hint) hint.textContent = input.value === '' ? 'Missing — enter a value; zero is valid.' :
        Number(input.value) === 0 ? 'Explicit zero' : 'Configured';
    }
    this.refreshSaveAvailability();
  };

  private handleOfferChange = (event: Event): void => {
    const input = event.currentTarget as HTMLInputElement;
    const trial = this.draft?.actualTrials[Number(input.dataset.trial)];
    const stock = input.dataset.offer!;
    if (!trial || !this.draft) return;
    if (input.checked) {
      trial.stocks!.push({ trialStockOfferId: null, stock, headCount: null, preEntryFee: null, dayOfFee: null });
      trial.stocks!.sort((a, b) => stockNames.indexOf(a.stock as typeof stockNames[number]) -
        stockNames.indexOf(b.stock as typeof stockNames[number]));
    } else {
      if (!window.confirm(`Remove ${stock} from this actual trial? Its count and both rates will be removed.`)) {
        input.checked = true;
        return;
      }
      trial.stocks = trial.stocks!.filter(offer => offer.stock !== stock);
    }
    syncOfferSummary(this.draft);
    this.updateRender();
    this.querySelector<HTMLInputElement>(`[data-trial="${input.dataset.trial}"][data-offer="${stock}"]`)?.focus();
  };

  private handleTextareaInput = (event: Event): void => {
    const target = event.currentTarget as HTMLTextAreaElement;
    const field = target.getAttribute('data-field') as keyof TrialEventUpsertRequestDto | null;
    if (!field) {
      return;
    }

    const value = target.value.trim().length === 0 ? null : target.value;
    this.updateDraftField(field, value);
  };

  private handleFileChange = (event: Event): void => {
    const target = event.currentTarget as HTMLInputElement;
    const file = target.files?.[0] ?? null;
    this.selectedPremiumPdf.set(file);
  };

  private handleNoteInput = (event: Event): void => {
    const target = event.currentTarget as HTMLTextAreaElement;
    this.noteDraft.set(target.value);

    if (this.noteSubmitError.get()) {
      this.noteSubmitError.set(null);
    }
  };

  private handleCourseDirectorInput = (event: Event): void => {
    const target = event.currentTarget as HTMLInputElement;
    this.courseDirectorEmailDraft.set(target.value);

    if (this.courseDirectorStatus.get()) {
      this.courseDirectorStatus.set(null);
    }
  };

  private handleBack = (): void => {
    navigate('/secretary');
  };

  private handleManageEntries = (): void => {
    const detail = this.detail.get();
    if (!detail) {
      return;
    }

    const params = new URLSearchParams();
    params.set('trialEventId', detail.trialEventId ?? '');
    if (detail.name) {
      params.set('trialName', detail.name);
    }

    navigate(`/secretary?${params.toString()}`);
  };

  private async handleSave(): Promise<void> {
    if (!this.draft || !this.canSave() || !this.draft.trialType) {
      return;
    }

    this.saving.set(true);
    this.actionError.set(null);
    this.validationErrors.set({});

    const request: TrialEventUpsertRequestDto = { ...this.draft, trialType: this.draft.trialType };
    const response = this.creating
      ? await adminApi.trialEvents.create(request, this.selectedPremiumPdf.get()!)
      : await adminApi.trialEvents.update(
      this.trialEventId,
      request,
      `"${this.detail.get()!.configurationVersion}"`,
      this.selectedPremiumPdf.get() ?? undefined
    );

    if (response.error) {
      this.saving.set(false);

      if (response.error.status === 403 || response.error.status === 401) {
        this.forbidden.set(true);
        return;
      }

      this.validationErrors.set(response.error.errors ?? {});
      this.actionError.set(response.error.status === 412
        ? 'This event changed after it was loaded. Your edits are still shown. Copy any changes you need, then reload before retrying.'
        : response.error.title || 'Unable to save changes.');
      return;
    }

    this.saving.set(false);
    if (this.creating) {
      if (!response.data?.trialEventId) {
        this.actionError.set('The server did not return a trial event identifier. Reload before retrying.');
        return;
      }
      navigate(`/admin/trial-events/${encodeURIComponent(response.data.trialEventId)}`);
      return;
    }
    await this.loadDetail();
  }

  private async handleToggleActive(): Promise<void> {
    const detail = this.detail.get();
    if (!detail?.trialEventId) {
      return;
    }

    const nextAction = detail.isActive ? 'deactivate' : 'reactivate';
    this.lifecycleAction.set(nextAction);
    this.actionError.set(null);

    const response = detail.isActive
      ? await adminApi.trialEvents.deactivate(detail.trialEventId)
      : await adminApi.trialEvents.reactivate(detail.trialEventId);

    this.lifecycleAction.set(null);

    if (response.error) {
      if (response.error.status === 403 || response.error.status === 401) {
        this.forbidden.set(true);
        return;
      }

      this.actionError.set(response.error.title || 'Unable to update the trial event lifecycle.');
      return;
    }

    await this.loadDetail();
  }

  private async handleCreateNote(): Promise<void> {
    if (!this.trialEventId) {
      return;
    }

    const content = this.noteDraft.get().trim();
    if (content.length === 0) {
      this.noteSubmitError.set('Enter a note before posting.');
      return;
    }

    this.noteSubmitting.set(true);
    this.noteSubmitError.set(null);

    const request: CreateTrialEventNoteRequestDto = { content };
    const response = await adminApi.trialEvents.createNote(this.trialEventId, request);

    this.noteSubmitting.set(false);

    if (response.error) {
      if (response.error.status === 403 || response.error.status === 401) {
        this.forbidden.set(true);
        return;
      }

      const firstFieldError = Object.values(response.error.errors ?? {}).flat()[0];
      this.noteSubmitError.set(firstFieldError || response.error.title || 'Unable to save trial-event note.');
      return;
    }

    if (!response.data) {
      this.noteSubmitError.set('Unable to save trial-event note.');
      return;
    }

    this.notes.set([response.data, ...this.notes.get()]);
    this.notesError.set(null);
    this.noteDraft.set('');
  }

  private updateCourseDirectors(
    nextCourseDirectors: TrialEventAdminDetailDto['courseDirectors']
  ): void {
    const detail = this.detail.get();
    if (!detail) {
      return;
    }

    this.detail.set({
      ...detail,
      courseDirectors: nextCourseDirectors ?? [],
    });
  }

  private getCourseDirectorStatus(
    courseDirector: NonNullable<TrialEventAdminDetailDto['courseDirectors']>[number]
  ): { variant: 'success' | 'warning' | 'info'; label: string } {
    if (courseDirector.userId) {
      return { variant: 'success', label: 'Assigned' };
    }

    if (courseDirector.inviteSent) {
      return { variant: 'warning', label: 'Invite sent' };
    }

    return { variant: 'info', label: 'Pending' };
  }

  private async handleAddCourseDirector(): Promise<void> {
    if (!this.trialEventId) {
      return;
    }

    const email = this.courseDirectorEmailDraft.get().trim();
    if (email.length === 0) {
      this.courseDirectorStatus.set({
        tone: 'error',
        message: 'Enter a course-director email before submitting.',
      });
      return;
    }

    this.courseDirectorSubmitting.set(true);
    this.courseDirectorStatus.set(null);

    const response = await adminApi.trialEvents.addCourseDirector(this.trialEventId, { email });

    this.courseDirectorSubmitting.set(false);

    if (response.error) {
      if (response.error.status === 403 || response.error.status === 401) {
        this.forbidden.set(true);
        return;
      }

      const firstFieldError = Object.values(response.error.errors ?? {}).flat()[0];
      this.courseDirectorStatus.set({
        tone: 'error',
        message:
          firstFieldError || response.error.title || 'Unable to update course directors for this trial event.',
      });
      return;
    }

    if (!response.data) {
      this.courseDirectorStatus.set({
        tone: 'error',
        message: 'Unable to update course directors for this trial event.',
      });
      return;
    }

    const addedCourseDirector = response.data;
    const existing = this.detail.get()?.courseDirectors ?? [];
    this.updateCourseDirectors([
      addedCourseDirector,
      ...existing.filter(
        (courseDirector) =>
          courseDirector.email.toLowerCase() !== addedCourseDirector.email.toLowerCase()
      ),
    ]);
    this.courseDirectorEmailDraft.set('');
    this.courseDirectorStatus.set({
      tone: 'success',
      message: addedCourseDirector.userId
        ? `Assigned ${addedCourseDirector.email} as a course director.`
        : addedCourseDirector.inviteSent
          ? `Invite sent to ${addedCourseDirector.email}. The assignment will complete after acceptance.`
          : `Updated ${addedCourseDirector.email} on the course-director roster.`,
    });
  }

  private async handleRemoveCourseDirector(email: string): Promise<void> {
    if (!this.trialEventId) {
      return;
    }

    this.courseDirectorRemovingEmail.set(email);
    this.courseDirectorStatus.set(null);

    const response = await adminApi.trialEvents.removeCourseDirector(this.trialEventId, email);

    this.courseDirectorRemovingEmail.set(null);

    if (response.error) {
      if (response.error.status === 403 || response.error.status === 401) {
        this.forbidden.set(true);
        return;
      }

      const firstFieldError = Object.values(response.error.errors ?? {}).flat()[0];
      this.courseDirectorStatus.set({
        tone: 'error',
        message:
          firstFieldError || response.error.title || 'Unable to update course directors for this trial event.',
      });
      return;
    }

    const existing = this.detail.get()?.courseDirectors ?? [];
    this.updateCourseDirectors(
      existing.filter(
        (courseDirector) => courseDirector.email.toLowerCase() !== email.toLowerCase()
      )
    );
    this.courseDirectorStatus.set({
      tone: 'success',
      message: `Removed ${email} from the course-director roster.`,
    });
  }

  private renderTextField(config: TextFieldConfig): string {
    const editableField = this.getEditableFieldName(config.field);
    const editable = this.isEditable(editableField);
    return `
      <div>
        <dt-input
          label="${config.label}"
          ${config.type ? `type="${config.type}"` : ''}
          ${this.creating && !config.nullable ? 'required' : ''}
          ${editable ? '' : 'readonly'}
          data-field="${String(config.field)}"
          data-editable-field="${editableField}"
        ></dt-input>
        ${!editable ? `<p class="mt-1 text-xs text-neutral-500">${this.esc(this.lockReason(editableField))}</p>` : ''}
      </div>
    `;
  }

  private renderNumberField(config: NumberFieldConfig): string {
    const editableField = this.getEditableFieldName(config.field);
    const editable = this.isEditable(editableField);
    const error = this.getFieldError(editableField);
    return `
      <label class="flex flex-col gap-1 text-sm font-medium text-neutral-700">
        <span>${config.label}</span>
        <input
          type="number"
          step="${config.step ?? '1'}"
          class="h-11 rounded-lg border ${error ? 'border-error-500 ring-1 ring-error-500' : 'border-neutral-300'} bg-white px-4 text-sm text-neutral-900 focus:border-primary-500 focus:outline-hidden focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:bg-neutral-100 disabled:text-neutral-500"
          data-field="${String(config.field)}"
          data-editable-field="${editableField}"
          ${config.nullable ? 'data-nullable="true"' : ''}
          ${editable ? '' : 'disabled'}
        />
        ${error ? `<span class="text-xs text-error-600">${this.esc(error)}</span>` : ''}
        ${!error && !editable ? `<span class="text-xs text-neutral-500">${this.esc(this.lockReason(editableField))}</span>` : ''}
      </label>
    `;
  }

  private renderCheckboxField(config: CheckboxFieldConfig): string {
    const editableField = this.getEditableFieldName(config.field);
    const editable = this.isEditable(editableField);
    return `
      <label class="flex items-start gap-3 rounded-lg border border-neutral-200 bg-white px-4 py-3 text-sm text-neutral-700 ${editable ? '' : 'bg-neutral-50'}">
        <input type="checkbox" aria-label="${this.esc(config.label)}" class="mt-1 h-4 w-4 rounded border-neutral-300 text-primary-500" data-field="${String(config.field)}" data-editable-field="${editableField}" ${editable ? '' : 'disabled'} />
        <span>
          <span class="font-medium text-neutral-900">${config.label}</span>
          ${!editable ? `<span class="mt-1 block text-xs text-neutral-500">${this.esc(this.lockReason(editableField))}</span>` : ''}
        </span>
      </label>
    `;
  }

  private renderReadOnlySummary(label: string, value: string): string {
    return `
      <div>
        <div class="text-xs font-semibold uppercase tracking-wide text-neutral-500">${label}</div>
        <div class="mt-1 text-sm text-neutral-800">${this.esc(value || '—')}</div>
      </div>
    `;
  }

  private formatNoteTimestamp(value: string): string {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }

    return parsed.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  private getEditableFieldName(field: keyof TrialEventUpsertRequestDto): string {
    return field.charAt(0).toUpperCase() + field.slice(1);
  }

  private getDraftValue(field: keyof TrialEventUpsertRequestDto): unknown {
    return this.draft?.[field] ?? null;
  }

  private renderActionBanner(): string {
    const actionError = this.actionError.get();
    const validationErrors = this.validationErrors.get();
    const hasValidationErrors = Object.keys(validationErrors).length > 0;

    if (!actionError && !hasValidationErrors) {
      return '';
    }

    return `
      <div role="alert" tabindex="-1" class="rounded-2xl border border-error-200 bg-error-50 p-4 text-sm text-error-700" data-testid="detail-error-banner">
        <div class="font-semibold">${this.esc(actionError || 'Please review the flagged fields before saving again.')}</div>
        ${
          hasValidationErrors
            ? `<ul class="mt-2 list-disc pl-5">
                ${Object.entries(validationErrors)
                  .map(([field, messages]) => `<li><button type="button" class="text-left underline break-words" data-error-field="${this.esc(field)}">${this.esc(field)}: ${this.esc(messages.join(' '))}</button></li>`)
                  .join('')}
              </ul>`
            : ''
        }
      </div>
    `;
  }

  private renderNotesList(): string {
    if (this.notesLoading.get()) {
      return `
        <div class="flex min-h-[160px] flex-col items-center justify-center gap-3" data-testid="notes-loading-state">
          <dt-spinner size="medium"></dt-spinner>
          <p class="text-sm text-neutral-600">Loading trial-event notes...</p>
        </div>
      `;
    }

    if (this.notesError.get()) {
      return `
        <div class="rounded-2xl border border-warning-200 bg-warning-50 p-4 text-sm text-warning-800" data-testid="notes-error-state">
          <div class="font-semibold">${this.esc(this.notesError.get() ?? 'Unable to load notes.')}</div>
          <p class="mt-1 text-xs text-warning-700">The rest of the trial-event detail page is still available while notes reload separately.</p>
          <dt-button class="mt-4" variant="outline" data-testid="retry-notes-btn">Retry notes</dt-button>
        </div>
      `;
    }

    if (this.notes.get().length === 0) {
      return `
        <div class="rounded-2xl border border-dashed border-neutral-300 bg-neutral-50 px-4 py-6 text-sm text-neutral-500" data-testid="notes-empty-state">
          No trial-event notes yet. Add the first note from this detail page.
        </div>
      `;
    }

    return `
      <div class="space-y-3" data-testid="notes-list">
        ${this.notes
          .get()
          .map(
            (note) => `
              <article class="rounded-2xl border border-neutral-200 bg-neutral-50 px-4 py-4" data-testid="note-item">
                <div class="flex flex-wrap items-center justify-between gap-2">
                  <div class="text-sm font-semibold text-neutral-900">${this.esc(note.authorEmail)}</div>
                  <div class="text-xs uppercase tracking-wide text-neutral-500">${this.esc(note.authorRole)} · ${this.esc(this.formatNoteTimestamp(note.createdAtUtc))}</div>
                </div>
                <p class="mt-3 whitespace-pre-wrap text-sm leading-6 text-neutral-700">${this.esc(note.content)}</p>
              </article>
            `
          )
          .join('')}
      </div>
    `;
  }

  private renderNotesCard(): string {
    return `
      <dt-card variant="outlined" data-testid="notes-card">
        <div slot="header">Trial Event Notes</div>
        <div class="grid gap-4 lg:grid-cols-[0.95fr_1.25fr]">
          <div class="rounded-2xl border border-neutral-200 bg-white p-4">
            <div class="text-sm font-semibold text-neutral-900">Add note</div>
            <p class="mt-2 text-sm text-neutral-600">Use notes for operational context that should stay reachable from the shared trial-event management surface.</p>
            ${
              this.noteSubmitError.get()
                ? `<div class="mt-4 rounded-2xl border border-error-200 bg-error-50 p-3 text-sm text-error-700" data-testid="note-submit-message" aria-live="polite">${this.esc(this.noteSubmitError.get() ?? '')}</div>`
                : ''
            }
            <label class="mt-4 flex flex-col gap-2 text-sm font-medium text-neutral-700">
              <span>Note content</span>
              <textarea
                class="min-h-[180px] rounded-lg border border-neutral-300 bg-white px-4 py-3 text-sm text-neutral-900 focus:border-primary-500 focus:outline-hidden focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:bg-neutral-100 disabled:text-neutral-500"
                data-testid="notes-textarea"
                aria-label="Trial event note content"
              ></textarea>
            </label>
            <div class="mt-4 flex flex-wrap items-center justify-between gap-3">
              <div class="text-xs text-neutral-500">Posting a note should not interrupt unsaved trial-event edits elsewhere on this page.</div>
              <dt-button variant="primary" data-testid="submit-note-btn">Add note</dt-button>
            </div>
          </div>
          <div class="rounded-2xl border border-neutral-200 bg-white p-4">
            <div class="text-sm font-semibold text-neutral-900">Recent notes</div>
            <div class="mt-4">${this.renderNotesList()}</div>
          </div>
        </div>
      </dt-card>
    `;
  }

  private renderCourseDirectorCard(detail: TrialEventAdminDetailDto): string {
    const courseDirectors = detail.courseDirectors ?? [];
    const status = this.courseDirectorStatus.get();

    return `
      <div class="rounded-2xl border border-neutral-200 bg-white p-4" data-testid="course-director-context">
        <div class="text-sm font-semibold text-neutral-900">Course directors</div>
        <p class="mt-2 text-sm text-neutral-600">Assign an existing course director or trigger the invite path by email. These actions update only the course-director roster and do not reset unsaved trial-event edits or the selected premium PDF replacement.</p>
        ${
          status
            ? `<div class="mt-4 rounded-2xl border ${status.tone === 'error' ? 'border-error-200 bg-error-50 text-error-700' : 'border-success-200 bg-success-50 text-success-700'} p-3 text-sm" data-testid="course-director-message" aria-live="polite">${this.esc(status.message)}</div>`
            : ''
        }
        <div class="mt-4 grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
          <label class="flex flex-col gap-2 text-sm font-medium text-neutral-700">
            <span>Course-director email</span>
            <input
              type="email"
              class="h-11 rounded-lg border border-neutral-300 bg-white px-4 text-sm text-neutral-900 focus:border-primary-500 focus:outline-hidden focus:ring-2 focus:ring-primary-500"
              data-testid="course-director-email-input"
              aria-label="Course director email"
              placeholder="coursedirector@example.com"
            />
          </label>
          <button
            class="inline-flex h-11 items-center justify-center rounded-lg bg-primary-600 px-4 text-sm font-semibold text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:bg-neutral-300"
            data-testid="add-course-director-btn"
            ${this.courseDirectorSubmitting.get() || this.courseDirectorEmailDraft.get().trim().length === 0 ? 'disabled' : ''}
          >
            ${this.courseDirectorSubmitting.get() ? 'Saving…' : 'Add or invite'}
          </button>
        </div>
        <div class="mt-4 space-y-3">
          ${
            courseDirectors.length > 0
              ? courseDirectors
                  .map((courseDirector) => {
                    const courseDirectorStatus = this.getCourseDirectorStatus(courseDirector);
                    const isRemoving =
                      this.courseDirectorRemovingEmail.get()?.toLowerCase() ===
                      courseDirector.email.toLowerCase();

                    return `
                      <div class="rounded-xl border border-neutral-200 bg-neutral-50 px-4 py-3" data-testid="course-director-item">
                        <div class="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <div class="text-sm font-medium text-neutral-900">${this.esc(courseDirector.email)}</div>
                            <div class="mt-1 text-xs text-neutral-500">Added ${this.esc(this.formatNoteTimestamp(courseDirector.createdAtUtc))}</div>
                          </div>
                          <div class="flex flex-wrap items-center gap-2">
                            <dt-badge variant="${courseDirectorStatus.variant}" data-testid="course-director-status">${courseDirectorStatus.label}</dt-badge>
                            <button
                              class="inline-flex h-9 items-center justify-center rounded-full border border-neutral-300 px-3 text-sm font-medium text-neutral-700 transition-colors hover:border-neutral-400 hover:bg-white disabled:cursor-not-allowed disabled:bg-neutral-100 disabled:text-neutral-400"
                              data-testid="remove-course-director-btn"
                              data-course-director-email="${this.esc(courseDirector.email)}"
                              ${isRemoving ? 'disabled' : ''}
                            >
                              ${isRemoving ? 'Removing…' : 'Remove'}
                            </button>
                          </div>
                        </div>
                      </div>
                    `;
                  })
                  .join('')
              : '<div class="rounded-2xl border border-dashed border-neutral-300 bg-neutral-50 px-4 py-6 text-sm text-neutral-500" data-testid="course-director-empty-state">No course directors are currently attached to this trial event.</div>'
          }
        </div>
      </div>
    `;
  }

  private renderLoading(): string {
    return `
      <div class="flex min-h-[320px] flex-col items-center justify-center gap-3" data-testid="loading-state">
        <dt-spinner size="large"></dt-spinner>
        <p class="text-sm text-neutral-600">Loading trial event details...</p>
      </div>
    `;
  }

  private renderError(message: string): string {
    return `
      <div class="flex min-h-[240px] flex-col items-center justify-center gap-4" data-testid="error-state">
        <div class="rounded-2xl border border-error-200 bg-error-50 p-6 text-center">
          <h2 class="text-lg font-semibold text-error-700">Unable to load trial event</h2>
          <p class="mt-2 text-sm text-error-600">${message}</p>
        </div>
      </div>
    `;
  }

  private renderForbidden(): string {
    return `
      <section class="px-4 py-12" data-testid="forbidden-state">
        <div class="mx-auto max-w-xl text-center">
          <h2 class="text-xl font-semibold text-neutral-900">Access restricted</h2>
          <p class="mt-2 text-sm text-neutral-600">Only Secretary and AppAdmin can edit trial-event management details.</p>
        </div>
      </section>
    `;
  }

  private renderConfigurationInput(
    label: string, path: string, indexPath: string, attributes: string, value: unknown, editable: boolean,
  ): string {
    const error = this.getFieldError(path) ?? this.getFieldError(indexPath);
    const descriptionId = `configuration-${indexPath.replace(/[^a-z0-9]/gi, '-')}`;
    return `<label class="flex min-w-0 flex-col gap-1 text-sm font-medium text-neutral-700">
      <span>${label}</span>
      <input ${attributes} value="${this.esc(value === null || value === undefined ? '' : String(value))}"
        aria-label="${this.esc(label)}" data-error-path="${this.esc(path)}" data-index-path="${this.esc(indexPath)}"
        aria-describedby="${descriptionId}" aria-invalid="${!!error}"
        class="h-11 w-full min-w-0 rounded-lg border border-neutral-300 px-3 disabled:bg-neutral-100"
        ${editable ? '' : 'disabled'} />
      <span id="${descriptionId}" class="text-xs ${error ? 'text-error-600' : 'text-neutral-500'}">
        ${error ? this.esc(error) : !editable ? this.esc(this.lockReason(path)) :
          attributes.includes('type="number"') ?
            `<span data-presence>${value === null ? 'Missing — enter a value; zero is valid.' : Number(value) === 0 ? 'Explicit zero' : 'Configured'}</span>` : ''}
      </span>
    </label>`;
  }

  private renderConfiguration(): string {
    const draft = this.draft!;
    const type = draft.trialType;
    const editable = this.isEditable('ActualTrials');
    return `<dt-card variant="outlined">
      <div slot="header">Trial Program and Actual Trials</div>
      <label class="flex flex-col gap-2 text-sm font-medium">
        <span>Trial Type</span>
        <select aria-label="Trial Type" data-testid="trial-type" data-error-path="trialType" aria-describedby="type-help"
          class="h-11 rounded-lg border border-neutral-300 bg-white px-3 disabled:bg-neutral-100"
          ${this.isEditable('TrialType') ? '' : 'disabled'}>
          <option value="" disabled ${!type ? 'selected' : ''}>Choose a trial type</option>
          ${['Arena', 'Farm', 'Ranch', 'PostAdvanced'].map(value =>
            `<option value="${value}" ${type === value ? 'selected' : ''}>${value === 'PostAdvanced' ? 'Post Advanced' : value}</option>`).join('')}
        </select>
        <span id="type-help" class="text-xs text-neutral-600">${this.isEditable('TrialType') ?
          'Choose explicitly. Required competitive divisions cannot be deselected.' : this.esc(this.lockReason('TrialType'))}</span>
      </label>
      <p role="status" class="mt-3 text-sm text-neutral-700">${this.esc(this.configurationNotice)}</p>
      ${type ? `<fieldset class="mt-4"><legend class="text-sm font-semibold">Competitive divisions</legend>
        <div class="mt-2 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        ${divisions.filter(division => (division.types as readonly string[]).includes(type)).map(division =>
          this.renderCheckboxField({ field: division.field, label: `${division.label}${division.required ? ' (required)' : ''}` })
            .replace('type="checkbox"', `type="checkbox" ${division.required ? 'data-required="true" disabled' : ''}`)).join('')}
        </div>
        <p class="mt-2 text-xs text-neutral-600">${type === 'Arena' ? 'Optional Post Advanced applies to Sheep/Cattle only, never Ducks.' :
          type === 'Farm' ? 'Mixed requires Farm, an offered FTD division, and an explicit Mixed stock offer. Mixed is one course, not commingled stock.' :
            'No competitive minimum is inferred. Select the division only if offered.'}</p>
        ${this.renderCheckboxField({ field: 'allowsFeo', label: 'Allows FEO — separate exhibition permission' })}
      </fieldset>` : ''}
      <div class="mt-6 flex flex-wrap items-center justify-between gap-3">
        <h2 class="text-lg font-semibold">Actual trials (${draft.actualTrials.length})</h2>
        <button type="button" data-add-trial class="rounded-lg border border-primary-500 px-4 py-2 disabled:opacity-50"
          ${editable && type ? '' : 'disabled'}>Add actual trial</button>
      </div>
      <p class="mt-2 text-sm text-neutral-600">Declare only real slots, in date/number order. No extra slot is inferred.
        Counts and both USD rates are independent of whether stock is offered. Zero is valid, not missing.</p>
      ${!editable ? `<p class="mt-2 text-sm text-neutral-600">${this.esc(this.lockReason('ActualTrials'))}</p>` : ''}
      <div class="mt-4 space-y-4">
      ${draft.actualTrials.map((trial, index) => {
        const path = this.trialPath(trial, index);
        const indexPath = `actualTrials[${index}]`;
        const slotEditable = this.creating || (trial.actualTrialId
          ? this.isEditable(`${path}.trialNumberOnDate`) : editable);
        const offersEditable = this.creating || (trial.actualTrialId
          ? this.isEditable(`${path}.stocks`) : editable);
        return `<fieldset class="min-w-0 rounded-xl border border-neutral-200 p-4">
          <legend class="px-2 font-semibold">Actual trial ${index + 1}</legend>
          <p class="mb-3 break-all text-xs text-neutral-500">ID: ${this.esc(trial.actualTrialId ?? 'Assigned by server on save')}</p>
          <div class="grid gap-3 sm:grid-cols-2">
          ${this.renderConfigurationInput('Trial date', `${path}.date`, `${indexPath}.date`,
            `type="date" data-trial="${index}" data-trial-field="date"`, trial.date,
            this.creating || (trial.actualTrialId ? this.isEditable(`${path}.date`) : editable))}
          <label class="flex flex-col gap-1 text-sm font-medium">
            <span>Trial number / AM-PM</span>
            <select aria-label="Trial number / AM-PM" class="h-11 rounded-lg border border-neutral-300 px-3 disabled:bg-neutral-100"
              data-trial="${index}" data-trial-field="trialNumberOnDate" data-error-path="${path}.trialNumberOnDate"
              data-index-path="${indexPath}.trialNumberOnDate" ${slotEditable ? '' : 'disabled'}>
              <option value="" ${!trial.trialNumberOnDate ? 'selected' : ''}>Choose a slot</option>
              <option value="1" ${trial.trialNumberOnDate === 1 ? 'selected' : ''}>1 / AM</option>
              <option value="2" ${trial.trialNumberOnDate === 2 ? 'selected' : ''}>2 / PM</option>
            </select>
            ${!slotEditable ? `<span class="text-xs text-neutral-500">${this.esc(this.lockReason(`${path}.trialNumberOnDate`))}</span>` : ''}
          </label></div>
          <fieldset class="mt-4"><legend class="text-sm font-semibold">Offered stock</legend>
            <div class="mt-2 flex flex-wrap gap-4">
            ${permittedStocks(type).map(stock => `<label class="flex items-center gap-2 text-sm">
              <input type="checkbox" data-trial="${index}" data-offer="${stock}" ${trial.stocks!.some(offer => offer.stock === stock) ? 'checked' : ''}
                ${offersEditable ? '' : 'disabled'} />${stock}</label>`).join('')}
            </div>
            ${!offersEditable ? `<p class="mt-2 text-xs text-neutral-500">${this.esc(this.lockReason(`${path}.stocks`))}</p>` : ''}
          </fieldset>
          ${trial.stocks!.map((offer, stockIndex) => {
            const stockPath = `${path}.stocks[${offer.stock}]`;
            const stockIndexPath = `${indexPath}.stocks[${stockIndex}]`;
            return `<fieldset class="mt-4 rounded-lg bg-neutral-50 p-3">
              <legend class="font-medium">${this.esc(offer.stock!)} configuration</legend>
              <p class="mb-3 break-all text-xs text-neutral-500">Offer ID: ${this.esc(offer.trialStockOfferId ?? 'Assigned by server on save')}</p>
              <div class="grid gap-3 md:grid-cols-3">
              ${(['headCount', 'preEntryFee', 'dayOfFee'] as const).map(field =>
                this.renderConfigurationInput(
                  field === 'headCount' ? 'Head count' : field === 'preEntryFee' ? 'Pre-entry rate (USD)' : 'Day-of rate (USD)',
                  `${stockPath}.${field}`, `${stockIndexPath}.${field}`,
                  `type="number" min="0" step="${field === 'headCount' ? '1' : '0.01'}" data-trial="${index}" data-stock="${stockIndex}" data-trial-field="${field}"`,
                  offer[field], this.creating || (offer.trialStockOfferId ? this.isEditable(`${stockPath}.${field}`) : editable))).join('')}
              </div>
            </fieldset>`;
          }).join('')}
          <button type="button" data-remove-trial="${index}" class="mt-4 rounded-lg border border-neutral-300 px-3 py-2 text-sm disabled:opacity-50"
            ${editable ? '' : 'disabled'}>Remove actual trial ${index + 1}</button>
        </fieldset>`;
      }).join('')}</div>
      <p class="mt-4 text-sm" aria-label="Event stock summary">${stockNames.map(stock =>
        `${stock}: ${draft[`offersStock${stock}`] ? 'offered' : 'not offered'}`).join(' · ')}</p>
    </dt-card>`;
  }

  private renderCreation(): string {
    return `<section class="px-4 py-6"><div class="mx-auto flex max-w-6xl flex-col gap-6">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <dt-button variant="ghost" data-testid="back-btn">Back to trial events</dt-button>
        <h1 class="text-2xl font-semibold">New trial event</h1>
        <dt-button variant="primary" data-testid="save-btn">Create trial event</dt-button>
      </div>
      <p class="text-sm text-neutral-600">Complete every required field, declare actual trials and upload the premium PDF before creating.
        Optional discounts and stock offers have no fallback values. No incomplete draft is saved.</p>
      <p role="status" data-completeness class="break-words text-sm text-neutral-600"></p>
      ${this.renderActionBanner()}
      <dt-card variant="outlined"><div slot="header">Identity and Form Template</div>
        <p class="mb-4 text-sm">Use an existing authorized form template's exact organization, sport, form and version.</p>
        <div class="grid gap-4 md:grid-cols-2 xl:grid-cols-3">${identityFields.map(field => this.renderTextField(field)).join('')}</div>
      </dt-card>
      <dt-card variant="outlined"><div slot="header">Operational Fields</div>
        <div class="grid gap-4 md:grid-cols-2 xl:grid-cols-3">${[...overviewFields, ...dateFields].map(field => this.renderTextField(field)).join('')}</div>
        <div class="mt-4 grid gap-3 md:grid-cols-2">${checkboxFields.map(field => this.renderCheckboxField(field)).join('')}</div>
        <div class="mt-4 grid gap-3 md:grid-cols-2">${['dayOfEntriesStartTime', 'dayOfEntriesEndTime'].map((field, index) =>
          `<label class="flex flex-col gap-1 text-sm"><span>Day-Of Entries ${index ? 'End' : 'Start'}</span>
            <input type="time" step="any" class="h-11 rounded-lg border border-neutral-300 px-3" data-field="${field}" /></label>`).join('')}</div>
        <label class="mt-4 flex flex-col gap-1 text-sm"><span>Address Directions</span>
          <textarea class="rounded-lg border border-neutral-300 p-3" data-field="addressDirections"></textarea></label>
      </dt-card>
      ${this.renderConfiguration()}
      <dt-card variant="outlined"><div slot="header">Venue, Contact, and Discounts</div>
        <div class="grid gap-4 md:grid-cols-2 xl:grid-cols-3">${venueFields.map(field => this.renderTextField(field)).join('')}
        ${numberFields.map(field => this.renderNumberField(field)).join('')}</div>
      </dt-card>
      <dt-card variant="outlined"><div slot="header">Mail Address and Premium PDF</div>
        <div class="grid gap-4 md:grid-cols-2 xl:grid-cols-3">${mailFields.map(field => this.renderTextField(field)).join('')}</div>
        <p class="mt-3 text-sm">Mail routing cannot be changed after creation.</p>
        <label class="mt-4 flex flex-col gap-2 text-sm"><span>Premium PDF (required)</span>
          <input type="file" accept="application/pdf,.pdf" data-testid="premium-pdf-input" /></label>
        <p class="mt-2 text-sm">${this.selectedPremiumPdf.get() ? `Selected: ${this.esc(this.selectedPremiumPdf.get()!.name)}` : 'Missing — select a premium PDF.'}</p>
      </dt-card>
    </div></section>`;
  }

  private renderDetail(detail: TrialEventAdminDetailDto): string {
    const isActive = detail.isActive ?? false;
    const premiumPdfEditable = this.isEditable('PremiumPdf');
    const premiumPdfError = this.getFieldError('premiumPdf');

    return `
      <section class="px-4 py-6" data-testid="trial-event-detail-page">
        <div class="mx-auto flex w-full max-w-6xl flex-col gap-6">
          <div class="flex flex-wrap items-center justify-between gap-3">
            <dt-button variant="ghost" data-testid="back-btn">Back to trial events</dt-button>
            <div class="flex flex-wrap items-center gap-2">
              <dt-badge variant="${isActive ? 'success' : 'warning'}" data-testid="active-badge">${isActive ? 'Active' : 'Inactive'}</dt-badge>
              <dt-button variant="outline" data-testid="manage-entries-btn">Manage entries</dt-button>
              <dt-button variant="outline" data-testid="toggle-active-btn">${isActive ? 'Deactivate' : 'Reactivate'}</dt-button>
              <dt-button variant="primary" data-testid="save-btn">Save changes</dt-button>
            </div>
          </div>

          ${this.renderActionBanner()}
          <p role="status" data-completeness class="break-words text-sm text-neutral-600"></p>
          ${this.renderConfiguration()}

          <dt-card variant="outlined">
            <div slot="header">Trial Event Detail</div>
            <div class="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              ${this.renderReadOnlySummary('Organization', detail.organizationCode ?? '—')}
              ${this.renderReadOnlySummary('Sport', detail.sportCode ?? '—')}
              ${this.renderReadOnlySummary('Form Template', `${detail.formCode ?? '—'} / ${detail.formVersion ?? '—'}`)}
              ${this.renderReadOnlySummary('Club Slug', detail.clubSlug ?? '—')}
              ${this.renderReadOnlySummary('Event Slug', detail.eventSlug ?? '—')}
              ${this.renderReadOnlySummary('Tracking Slug', detail.trackingSlug ?? '—')}
            </div>
          </dt-card>

          <dt-card variant="outlined">
            <div slot="header">Operational Fields</div>
            <div class="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              ${overviewFields.map((field) => this.renderTextField(field)).join('')}
              ${dateFields.map((field) => this.renderTextField(field)).join('')}
            </div>
            <div class="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <label class="flex flex-col gap-1 text-sm font-medium text-neutral-700">
                <span>Day-Of Entries Start</span>
                <input type="time" step="any" class="h-11 rounded-lg border border-neutral-300 bg-white px-4 text-sm text-neutral-900 focus:border-primary-500 focus:outline-hidden focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:bg-neutral-100 disabled:text-neutral-500" data-field="dayOfEntriesStartTime" data-editable-field="DayOfEntriesStartTime" ${this.isEditable('DayOfEntriesStartTime') ? '' : 'disabled'} />
                ${!this.isEditable('DayOfEntriesStartTime') ? `<span class="text-xs text-neutral-500">${this.esc(this.lockReason('DayOfEntriesStartTime'))}</span>` : ''}
              </label>
              <label class="flex flex-col gap-1 text-sm font-medium text-neutral-700">
                <span>Day-Of Entries End</span>
                <input type="time" step="any" class="h-11 rounded-lg border border-neutral-300 bg-white px-4 text-sm text-neutral-900 focus:border-primary-500 focus:outline-hidden focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:bg-neutral-100 disabled:text-neutral-500" data-field="dayOfEntriesEndTime" data-editable-field="DayOfEntriesEndTime" ${this.isEditable('DayOfEntriesEndTime') ? '' : 'disabled'} />
                ${!this.isEditable('DayOfEntriesEndTime') ? `<span class="text-xs text-neutral-500">${this.esc(this.lockReason('DayOfEntriesEndTime'))}</span>` : ''}
              </label>
            </div>
            <div class="mt-4">
              <label class="flex flex-col gap-1 text-sm font-medium text-neutral-700">
                <span>Address Directions</span>
                <textarea class="min-h-[120px] rounded-lg border border-neutral-300 bg-white px-4 py-3 text-sm text-neutral-900 focus:border-primary-500 focus:outline-hidden focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:bg-neutral-100 disabled:text-neutral-500" data-field="addressDirections" data-editable-field="AddressDirections" ${this.isEditable('AddressDirections') ? '' : 'disabled'}></textarea>
                ${!this.isEditable('AddressDirections') ? `<span class="text-xs text-neutral-500">${this.esc(this.lockReason('AddressDirections'))}</span>` : ''}
              </label>
            </div>
          </dt-card>

          <dt-card variant="outlined">
            <div slot="header">Venue, Contact, and Fees</div>
            <div class="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              ${venueFields.map((field) => this.renderTextField(field)).join('')}
              ${numberFields.map((field) => this.renderNumberField(field)).join('')}
            </div>
          </dt-card>

          <dt-card variant="outlined">
            <div slot="header">Booleans and Class Flags</div>
            <div class="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              ${checkboxFields.map((field) => this.renderCheckboxField(field)).join('')}
            </div>
          </dt-card>

          <dt-card variant="outlined">
            <div slot="header">Read-Only Mail Address and Premium PDF</div>
            <div class="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              ${this.renderReadOnlySummary('Entry Mail Address Line 1', detail.entryMailAddressLine1 ?? '—')}
              ${this.renderReadOnlySummary('Entry Mail Address Line 2', detail.entryMailAddressLine2 ?? '—')}
              ${this.renderReadOnlySummary('Entry Mail City', detail.entryMailAddressCity ?? '—')}
              ${this.renderReadOnlySummary('Entry Mail State', detail.entryMailAddressState ?? '—')}
              ${this.renderReadOnlySummary('Entry Mail Postal Code', detail.entryMailAddressPostalCode ?? '—')}
              ${this.renderReadOnlySummary('Created', detail.createdAtUtc ?? '—')}
            </div>
            <div class="mt-4 rounded-2xl border border-neutral-200 bg-neutral-50 p-4" data-testid="premium-pdf-card">
              <div class="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div class="text-sm font-semibold text-neutral-900">Premium PDF</div>
                  <div class="mt-1 text-sm text-neutral-600">
                    ${
                      detail.premiumPdfUrl
                        ? `<a class="text-primary-600 underline" href="${this.esc(detail.premiumPdfUrl)}" target="_blank" rel="noreferrer">Open current premium PDF</a>`
                        : 'No premium PDF URL available.'
                    }
                  </div>
                </div>
                ${
                  premiumPdfEditable
                    ? `
                  <label class="flex flex-col gap-1 text-sm font-medium text-neutral-700">
                    <span>Replace PDF</span>
                    <input type="file" accept="application/pdf" class="text-sm text-neutral-700" data-testid="premium-pdf-input" />
                    ${premiumPdfError ? `<span class="text-xs text-error-600">${this.esc(premiumPdfError)}</span>` : ''}
                  </label>
                `
                    : `<div class="text-xs text-neutral-500">${this.esc(this.lockReason('PremiumPdf'))}</div>`
                }
              </div>
              ${
                this.selectedPremiumPdf.get()
                  ? `<div class="mt-3 text-xs text-neutral-600" data-testid="selected-premium-pdf">Selected replacement: ${this.esc(this.selectedPremiumPdf.get()?.name ?? '')}</div>`
                  : ''
              }
            </div>
          </dt-card>

          ${this.renderNotesCard()}

          <dt-card variant="outlined">
            <div slot="header">Entries and Course Director Context</div>
            <div class="grid gap-4 lg:grid-cols-[1fr_1.2fr]">
              <div class="rounded-2xl border border-neutral-200 bg-white p-4">
                <div class="text-sm font-semibold text-neutral-900">Entry workflow</div>
                <p class="mt-2 text-sm text-neutral-600">Use the existing secretary entry workflow for entry review, export, and entry-detail navigation within this trial event.</p>
                <dt-button variant="outline" class="mt-4" data-testid="manage-entries-inline-btn">Manage entries</dt-button>
              </div>
              ${this.renderCourseDirectorCard(detail)}
            </div>
          </dt-card>
        </div>
      </section>
    `;
  }

  private updateRender(): void {
    let content = '';

    if (this.pageState.get() === 'forbidden') {
      content = this.renderForbidden();
    } else if (this.pageState.get() === 'loading') {
      content = this.renderLoading();
    } else if (this.pageState.get() === 'error') {
      content = this.renderError(this.loadError.get() ?? 'Unable to load trial event details.');
    } else {
      const detail = this.detail.get();
      content = this.creating ? this.renderCreation() : detail ? this.renderDetail(detail) : '';
    }

    this.render(content);
    this.attachEventListeners();
    this.assignFormValues();
    this.refreshSaveAvailability();
  }

  private attachEventListeners(): void {
    if (this.pageState.get() !== 'loaded') {
      return;
    }
    this.$('[data-testid="trial-type"]')?.addEventListener('change', this.handleTypeChange);
    this.$$('[data-trial-field]').forEach(input => input.addEventListener('input', this.handleTrialInput));
    this.$$('[data-offer]').forEach(input => input.addEventListener('change', this.handleOfferChange));
    this.$('[data-add-trial]')?.addEventListener('click', () => {
      if (!this.draft) return;
      this.draft.actualTrials.push({ actualTrialId: null, date: null, trialNumberOnDate: null, stocks: [] });
      syncOfferSummary(this.draft);
      this.updateRender();
      this.querySelector<HTMLInputElement>(`[data-trial="${this.draft.actualTrials.length - 1}"][data-trial-field="date"]`)?.focus();
    });
    this.$$('[data-remove-trial]').forEach(button => button.addEventListener('click', () => {
      if (!this.draft || !window.confirm('Remove this actual trial and all its stock counts and rates?')) return;
      this.draft.actualTrials.splice(Number(button.dataset.removeTrial), 1);
      syncOfferSummary(this.draft);
      this.updateRender();
      this.$('[data-add-trial]')?.focus();
    }));
    this.$$('[data-error-field]').forEach(button => button.addEventListener('click', () => {
      const field = button.dataset.errorField!;
      const control = [...this.querySelectorAll<HTMLElement>('[data-error-path], [data-field], [data-testid="premium-pdf-input"]')]
        .find(input => input.dataset.errorPath === field || input.dataset.indexPath === field ||
          input.dataset.field?.toLowerCase() === field.toLowerCase() ||
          field === 'premiumPdf' && input.dataset.testid === 'premium-pdf-input');
      (control?.querySelector<HTMLElement>('input') ?? control)?.focus();
    }));

    const backBtn = this.$('[data-testid="back-btn"]');
    if (backBtn) {
      backBtn.addEventListener('click', this.handleBack);
    }

    const manageEntriesBtn = this.$('[data-testid="manage-entries-btn"]');
    if (manageEntriesBtn) {
      manageEntriesBtn.addEventListener('click', this.handleManageEntries);
    }

    const inlineManageEntriesBtn = this.$('[data-testid="manage-entries-inline-btn"]');
    if (inlineManageEntriesBtn) {
      inlineManageEntriesBtn.addEventListener('click', this.handleManageEntries);
    }

    const saveBtn = this.$('[data-testid="save-btn"]');
    if (saveBtn) {
      saveBtn.addEventListener('click', () => {
        void this.handleSave();
      });
    }

    const toggleActiveBtn = this.$('[data-testid="toggle-active-btn"]');
    if (toggleActiveBtn) {
      toggleActiveBtn.addEventListener('click', () => {
        void this.handleToggleActive();
      });
    }

    this.$$('dt-input[data-field]').forEach((input) => {
      input.addEventListener('input', this.handleTextInput);
    });

    this.$$('input[type="number"][data-field]').forEach((input) => {
      input.addEventListener('input', this.handleNumberInput);
    });

    this.$$('input[type="time"][data-field]').forEach((input) => {
      input.addEventListener('input', this.handleTimeInput);
    });

    this.$$('input[type="checkbox"][data-field]').forEach((input) => {
      input.addEventListener('change', this.handleCheckboxInput);
    });

    const directions = this.$('textarea[data-field="addressDirections"]');
    if (directions) {
      directions.addEventListener('input', this.handleTextareaInput);
    }

    const premiumPdfInput = this.$('[data-testid="premium-pdf-input"]');
    if (premiumPdfInput) {
      premiumPdfInput.addEventListener('change', this.handleFileChange);
    }

    const notesTextarea = this.$('[data-testid="notes-textarea"]');
    if (notesTextarea) {
      notesTextarea.addEventListener('input', this.handleNoteInput);
    }

    const submitNoteBtn = this.$('[data-testid="submit-note-btn"]');
    if (submitNoteBtn) {
      submitNoteBtn.addEventListener('click', () => {
        void this.handleCreateNote();
      });
    }

    const retryNotesBtn = this.$('[data-testid="retry-notes-btn"]');
    if (retryNotesBtn) {
      retryNotesBtn.addEventListener('click', () => {
        void this.loadNotes();
      });
    }

    const courseDirectorEmailInput = this.$('[data-testid="course-director-email-input"]');
    if (courseDirectorEmailInput) {
      courseDirectorEmailInput.addEventListener('input', this.handleCourseDirectorInput);
    }

    const addCourseDirectorBtn = this.$('[data-testid="add-course-director-btn"]');
    if (addCourseDirectorBtn) {
      addCourseDirectorBtn.addEventListener('click', () => {
        void this.handleAddCourseDirector();
      });
    }

    this.$$('[data-testid="remove-course-director-btn"]').forEach((button) => {
      button.addEventListener('click', () => {
        const email = button.getAttribute('data-course-director-email');
        if (email) {
          void this.handleRemoveCourseDirector(email);
        }
      });
    });
  }

  private assignFormValues(): void {
    if (this.pageState.get() !== 'loaded' || !this.draft) {
      return;
    }

    this.$$('dt-input[data-field]').forEach((input) => {
      const fieldName = input.getAttribute('data-field') as keyof TrialEventUpsertRequestDto | null;
      const editableField = input.getAttribute('data-editable-field');
      if (!fieldName || !editableField) {
        return;
      }

      const value = this.getDraftValue(fieldName);
      const dtInput = input as InputElement;
      dtInput.value = typeof value === 'string' ? value : value ? String(value) : '';
      dtInput.error = this.getFieldError(editableField);
    });

    this.$$('input[type="number"][data-field], input[type="time"][data-field]').forEach((input) => {
      const nativeInput = input as HTMLInputElement;
      const fieldName = nativeInput.getAttribute('data-field') as keyof TrialEventUpsertRequestDto | null;
      if (!fieldName) {
        return;
      }

      const value = this.getDraftValue(fieldName);
      nativeInput.value = value === null || value === undefined ? '' : String(value);
    });

    this.$$('input[type="checkbox"][data-field]').forEach((input) => {
      const nativeInput = input as HTMLInputElement;
      const fieldName = nativeInput.getAttribute('data-field') as keyof TrialEventUpsertRequestDto | null;
      if (!fieldName) {
        return;
      }

      nativeInput.checked = Boolean(this.getDraftValue(fieldName));
      if (nativeInput.hasAttribute('data-required')) nativeInput.disabled = true;
    });

    const directions = this.$('textarea[data-field="addressDirections"]') as HTMLTextAreaElement | null;
    if (directions) {
      directions.value = (this.getDraftValue('addressDirections') as string | null) ?? '';
    }

    const notesTextarea = this.$('[data-testid="notes-textarea"]') as HTMLTextAreaElement | null;
    if (notesTextarea) {
      notesTextarea.value = this.noteDraft.get();
    }

    const courseDirectorEmailInput = this.$('[data-testid="course-director-email-input"]') as HTMLInputElement | null;
    if (courseDirectorEmailInput) {
      courseDirectorEmailInput.value = this.courseDirectorEmailDraft.get();
    }

    const saveBtn = this.$('[data-testid="save-btn"]') as DtButtonElement | null;
    if (saveBtn) {
      saveBtn.loading = this.saving.get();
      saveBtn.disabled = !this.canSave();
    }

    const toggleActiveBtn = this.$('[data-testid="toggle-active-btn"]') as DtButtonElement | null;
    if (toggleActiveBtn) {
      toggleActiveBtn.loading = this.lifecycleAction.get() !== null;
      toggleActiveBtn.disabled = this.saving.get() || this.lifecycleAction.get() !== null;
    }

    const submitNoteBtn = this.$('[data-testid="submit-note-btn"]') as DtButtonElement | null;
    if (submitNoteBtn) {
      submitNoteBtn.loading = this.noteSubmitting.get();
      submitNoteBtn.disabled = this.noteSubmitting.get() || this.noteDraft.get().trim().length === 0;
    }
  }
}

customElements.define('dt-trial-event-detail-page', TrialEventDetailPage);